/**
 * Home page copy, TR / EN / DE.
 *
 * Every string is lifted VERBATIM from the former App.jsx, AppEN.jsx and
 * AppDE.jsx (JSX line wraps collapsed to single spaces). The only new strings
 * are the spec-table keys (`about.facts[].key`), written for the title block that replaces the metric card, feature tiles and badges,
 * and the EN / DE `seo` blocks (the TR title and description are index.html's
 * former ones; EN / DE restate the TR description's facts).
 *
 * Paragraphs with emphasis are stored as { before, strong, after } so the
 * <strong> run survives without HTML in data. The DE paragraphs used literal
 * markdown asterisks (rendered as "**" on the old page); they are <strong> here.
 * The EN/DE gallery descriptions are a deliberate change requested by the lead:
 * translations of the TR line (the old copy described a removed lens effect).
 */

/** Gallery files, in the order the TR home page showed them. */
export const HOME_GALLERY_IMAGES = Object.freeze([
  'portfolio-new-1.webp',
  'portfolio-7.webp',
  'portfolio-11.webp',
  'portfolio-new-4.webp',
  'portfolio-10.webp',
  'portfolio-2.webp',
  'portfolio-3.webp',
])

export const HOME_I18N = {
  tr: {
    seo: {
      title: 'Ankaplastik | Endüstriyel Tampon & Serigrafi Baskı Çözümleri - Bursa',
      description:
        "Bursa'da otomotiv ve endüstriyel sektörler için profesyonel Tampon Baskı, Serigrafi Baskı ve Parça Montaj hizmetleri. 25+ yıllık tecrübe ile ISO 9001:2015 sertifikalı üretim.",
      keywords:
        'ankaplastik, tampon baskı, serigrafi baskı, pad printing, screen printing, bursa baskı, endüstriyel baskı, parça montaj, trim parçaları, otomotiv baskı, plastik baskı, ISO 9001',
    },
    services: {
      title: 'Endüstriyel Baskı Çözümleri',
      description:
        'Modern makine parkurumuz ve uzman kadromuzla otomotiv sektörünün ihtiyaçlarına özel baskı çözümleri sunuyoruz.',
      items: [
        {
          key: 'pad',
          name: 'Tampon Baskı',
          description:
            '3D yüzeylere mükemmel uyum sağlayan tampon baskı teknolojimizle karmaşık geometrilere hassas baskı yapıyoruz.',
          features: [
            'Çok renkli baskı kapasitesi',
            'Mikron düzeyinde hassasiyet',
            'UV dayanıklı mürekkepler',
            'Otomatik üretim hattı',
          ],
        },
        {
          key: 'screen',
          name: 'Serigrafi Baskı',
          description:
            'Geniş yüzeyler için ideal olan serigrafi baskı ile yüksek hacimli üretimlerinizi kaliteden ödün vermeden gerçekleştiriyoruz.',
          features: [
            'Büyük alan baskı kapasitesi',
            'Yüksek mürekkep kalınlığı',
            'Metalik ve özel efektler',
            'Seri üretim optimizasyonu',
          ],
        },
        {
          key: 'assembly',
          name: 'Montaj ve Trim Parçaları',
          description:
            'Dekoratif trim baskılarının yanı sıra, çok bileşenli parçaların ve küçük aksamların hassas montajını (assembly) bünyemizde gerçekleştiriyoruz.',
          features: [
            'Hassas parça montajı',
            'Yarı mamul birleştirme',
            'Dekoratif trim uygulamaları',
            '%100 Fonksiyonel kontrol',
          ],
        },
      ],
    },
    gallery: {
      title: 'Galeri',
      description: 'Üretim teknolojilerimizden ve tamamlanan projelerimizden seçkiler.',
    },
    about: {
      titleLines: ['Endüstriyel Hizmetlerde', '25 Yıllık Deneyim'],
      paragraphs: [
        {
          before: "1999'dan bu yana endüstriyel üretim sektöründe faaliyet gösteren Ankaplastik, ",
          strong: 'son on yılda otomotiv yan sanayine',
          after: ' odaklanarak global tedarik zincirinin güvenilir bir parçası haline geldi.',
        },
        {
          before: 'Uluslararası otomotiv gruplarına ',
          strong: 'onaylı tedarikçi',
          after:
            ' olarak hizmet veriyor; tampon baskı, serigrafi ve parça montajı alanlarında araç içi trim parçalarından güvenlik bileşenlerine kadar geniş bir ürün yelpazesinde üretim gerçekleştiriyoruz.',
        },
      ],
      facts: [
        { key: 'Deneyim', value: '25+ Yıllık Tecrübe' },
        { key: 'Tedarik', value: 'Global Onaylı Tedarikçi' },
        { key: 'Teslimat', value: 'Zamanında Teslimat' },
        { key: 'Kalite', value: 'ISO 9001:2015 Sertifikalı' },
        { key: 'Hata oranı', value: '%0.01 Hata Oranı' },
        { key: 'Denetim', value: 'Uluslararası Denetim Onaylı' },
      ],
    },
    contact: {
      title: 'Projenizi Konuşalım',
      description:
        'Baskı ihtiyaçlarınız için bizimle iletişime geçin. Size özel çözümler sunmaktan memnuniyet duyarız.',
    },
  },

  en: {
    seo: {
      title: 'Ankaplastik | Pad & Screen Printing, Assembly – Bursa',
      description:
        'Pad printing, screen printing and assembly for automotive and industrial parts, from Bursa, Türkiye. 25+ years of experience, ISO 9001:2015 certified.',
    },
    services: {
      title: 'Industrial Printing Solutions',
      description:
        'State-of-the-art machinery and expert processes delivering tailored printing solutions for the automotive sector.',
      items: [
        {
          key: 'pad',
          name: 'Pad Printing',
          description:
            'Advanced pad printing technology delivering flawless results on complex 3D surfaces and irregular geometries.',
          features: [
            'Multi-color printing capability',
            'Micron-level precision',
            'UV-resistant inks',
            'Automated production lines',
          ],
        },
        {
          key: 'screen',
          name: 'Screen Printing',
          description:
            'High-volume screen printing ideal for large surfaces, delivering exceptional quality without compromise.',
          features: [
            'Large area coverage',
            'Superior ink deposit',
            'Metallic and special effects',
            'High-volume optimization',
          ],
        },
        {
          key: 'assembly',
          name: 'Assembly & Trim Parts',
          description:
            'Precision assembly of multi-component parts and decorative trim applications for automotive interiors.',
          features: [
            'Precision component assembly',
            'Sub-assembly integration',
            'Decorative trim applications',
            '100% functional testing',
          ],
        },
      ],
    },
    gallery: {
      title: 'Refined Craftsmanship',
      // Deliberate copy change (lead, 2026-10-03): the old line described a lens UI that no longer exists.
      description: 'A selection from our production technologies and completed projects.',
    },
    about: {
      titleLines: ['25 Years of Industrial Excellence'],
      paragraphs: [
        {
          before:
            "Since 1999, Ankaplastik has been a trusted name in industrial manufacturing. Over the past decade, we've ",
          strong: 'specialized in automotive tier-2 supply',
          after: ', becoming a reliable partner in the global supply chain.',
        },
        {
          before: 'As an ',
          strong: 'approved supplier to international automotive groups',
          after:
            ', we deliver pad printing, screen printing, and precision assembly services for interior trim parts and safety components.',
        },
      ],
      facts: [
        { key: 'Experience', value: '25+ Years of Experience' },
        { key: 'Supplier', value: 'Globally Approved Supplier' },
        { key: 'Delivery', value: 'On-Time Delivery' },
        { key: 'Quality', value: 'ISO 9001:2015 Certified' },
        { key: 'Defect rate', value: '0.01% Defect Rate' },
        { key: 'Audits', value: 'International Audit Approved' },
      ],
    },
    contact: {
      title: "Let's Discuss Your Project",
      description:
        "Get in touch for your printing requirements. We'd be delighted to provide tailored solutions.",
    },
  },

  de: {
    seo: {
      title: 'Ankaplastik | Tampondruck, Siebdruck & Montage – Bursa',
      description:
        'Tampondruck, Siebdruck und Montage für Automobil- und Industrieteile aus Bursa, Türkei. Über 25 Jahre Erfahrung, nach ISO 9001:2015 zertifiziert.',
    },
    services: {
      title: 'Industrielle Drucklösungen',
      description:
        'Modernste Maschinen und Expertenprozesse für maßgeschneiderte Drucklösungen im Automobilsektor.',
      items: [
        {
          key: 'pad',
          name: 'Tampondruck',
          description:
            'Fortschrittliche Tampondrucktechnologie für makellose Ergebnisse auf komplexen 3D-Oberflächen und unregelmäßigen Geometrien.',
          features: [
            'Mehrfarbendruck-Fähigkeit',
            'Präzision auf Mikrometerebene',
            'UV-beständige Farben',
            'Automatisierte Produktionslinien',
          ],
        },
        {
          key: 'screen',
          name: 'Siebdruck',
          description:
            'Volumenstarker Siebdruck, ideal für große Oberflächen, liefert außergewöhnliche Qualität ohne Kompromisse.',
          features: [
            'Großflächenabdeckung',
            'Überlegener Farbauftrag',
            'Effekt- und Spezialfarben',
            'Serienfertigungs-Optimierung',
          ],
        },
        {
          key: 'assembly',
          name: 'Montage & Trimteile',
          description:
            'Präzisionsmontage von Mehrkomponententeilen und dekorativen Trim-Anwendungen für Fahrzeuginnenräume.',
          features: [
            'Präzisionsmontage von Komponenten',
            'Baugruppenintegration',
            'Dekorative Trim-Anwendungen',
            '100% Funktionsprüfung',
          ],
        },
      ],
    },
    gallery: {
      title: 'Raffinierte Handwerkskunst',
      // Deliberate copy change (lead, 2026-10-03): the old line described a lens UI that no longer exists.
      description: 'Eine Auswahl aus unseren Fertigungstechnologien und abgeschlossenen Projekten.',
    },
    about: {
      titleLines: ['25 Jahre industrielle Exzellenz'],
      paragraphs: [
        {
          before:
            'Seit 1999 ist Ankaplastik ein vertrauenswürdiger Name in der industriellen Fertigung. Im letzten Jahrzehnt haben wir uns auf die ',
          strong: 'Automobil-Tier-2-Zulieferung spezialisiert',
          after: ' und sind ein zuverlässiger Partner in der globalen Lieferkette.',
        },
        {
          before: 'Als ',
          strong: 'zugelassener Lieferant internationaler Automobilgruppen',
          after:
            ' liefern wir Tampondruck, Siebdruck und Präzisionsmontage für Innenraumteile und Sicherheitskomponenten.',
        },
      ],
      facts: [
        { key: 'Erfahrung', value: '25+ Jahre Erfahrung' },
        { key: 'Lieferant', value: 'Weltweit zugelassener Lieferant' },
        { key: 'Lieferung', value: 'Pünktliche Lieferung' },
        { key: 'Qualität', value: 'ISO 9001:2015 Zertifiziert' },
        { key: 'Fehlerquote', value: '0,01% Fehlerquote' },
        { key: 'Audits', value: 'International auditiert' },
      ],
    },
    contact: {
      title: 'Lassen Sie uns über Ihr Projekt sprechen',
      description:
        'Kontaktieren Sie uns für Ihre Druckanforderungen. Wir bieten Ihnen gerne maßgeschneiderte Lösungen an.',
    },
  },
}
